import React from 'react';
import classNames from 'classnames';
import { useIntl } from '../../../../util/reactIntl';
import css from './HowItWorks.module.css';

/**
 * HowItWorks — three-step explainer rendered on the customer landing page.
 *
 * Visual match for lovable_template/src/components/site/HowItWorks.tsx:
 *  - Centered title + subtitle
 *  - Three white cards (border, rounded), each with a faint "0N" mark,
 *    a yellow icon tile, a bold title and a muted description.
 *  - Static / non-interactive cards (no links, no data fetching).
 *
 * Icons are inlined as SVGs (no new dependency); shapes mirror the
 * Lucide Search / MousePointerClick / Smile glyphs used in Lovable.
 */

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const MousePointerClickIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 4.1 12 6" />
    <path d="m5.1 8-2.9-.8" />
    <path d="m6 12-1.9 2" />
    <path d="M7.2 2.2 8 5.1" />
    <path d="M9.037 9.69a.498.498 0 0 1 .653-.653l11 4.5a.5.5 0 0 1-.074.949l-4.349 1.041a1 1 0 0 0-.74.739l-1.04 4.35a.5.5 0 0 1-.95.074z" />
  </svg>
);

const SmileIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);

const STEPS = [
  {
    key: 'discover',
    Icon: SearchIcon,
    titleId: 'HowItWorks.step1Title',
    descriptionId: 'HowItWorks.step1Description',
  },
  {
    key: 'book',
    Icon: MousePointerClickIcon,
    titleId: 'HowItWorks.step2Title',
    descriptionId: 'HowItWorks.step2Description',
  },
  {
    key: 'enjoy',
    Icon: SmileIcon,
    titleId: 'HowItWorks.step3Title',
    descriptionId: 'HowItWorks.step3Description',
  },
];

const HowItWorks = () => {
  const intl = useIntl();

  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  return (
    <section
      id="how-it-works"
      className={classNames(
        css.howItWorksWrapper,
        arabicLanguage && css.arabicText
      )}
    >
      <div className={css.intro}>
        <h2 className={css.heading}>
          {intl.formatMessage({ id: 'HowItWorks.heading' })}
        </h2>
        <p className={css.subheading}>
          {intl.formatMessage({ id: 'HowItWorks.subheading' })}
        </p>
      </div>

      <div className={css.stepsGrid}>
        {STEPS.map((step, index) => {
          const number = String(index + 1).padStart(2, '0');
          const { Icon } = step;
          return (
            <article key={step.key} className={css.stepCard}>
              <span className={css.stepNumber} aria-hidden="true">
                {number}
              </span>
              <span className={css.iconTile}>
                <Icon />
              </span>
              <h3 className={css.stepTitle}>
                {intl.formatMessage({ id: step.titleId })}
              </h3>
              <p className={css.stepDescription}>
                {intl.formatMessage({ id: step.descriptionId })}
              </p>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default HowItWorks;
